import React from 'react';
import { Box, Container, Link, Typography } from '@mui/material';
import PublicNavbar from '../../components/public/PublicNavbar';
import { rentalUnderwritingEnabled } from '../../config/featureFlags';

const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL || '';

const LegalLayout: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
    <PublicNavbar />
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="overline" color="text.secondary">
        Pending legal review
      </Typography>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 800, mb: 2 }}>
        {title}
      </Typography>
      {children}
    </Container>
  </Box>
);

export const PrivacyPage: React.FC = () => (
  <LegalLayout title="Privacy Policy">
    <Typography paragraph>
      This draft explains what DealSkout stores so you can sign in and analyze deals: your account,
      the deal assumptions you save, comparable sales you import or retrieve, and billing status from
      Stripe. It is not a finished privacy policy.
    </Typography>
    <Typography paragraph>
      Deal financial details, property addresses, and AI review text stay in DealSkout. Product
      analytics records only event names such as whether a tool was opened, not the contents of a deal.
    </Typography>
  </LegalLayout>
);

export const TermsPage: React.FC = () => (
  <LegalLayout title="Terms of Service">
    <Typography paragraph>
      DealSkout is a modeling tool. You are responsible for the assumptions you enter and for
      decisions you make about a property. These draft terms do not create a warranty that a deal
      will perform as modeled.
    </Typography>
    <Typography paragraph>
      Accounts are individual. You may not access another person’s deals. Paid plans, when offered,
      follow the billing policy and the allowances shown in the product.
    </Typography>
  </LegalLayout>
);

export const DisclaimerPage: React.FC = () => (
  <LegalLayout title="Investment disclaimer">
    <Typography paragraph>
      Profit, cash required, returns, break-even, comparable-sale indications, modeled grades, and
      stress tests are calculated from your inputs. They are not an appraisal, a promise of profit,
      or a recommendation to purchase.
    </Typography>
    <Typography paragraph>
      {rentalUnderwritingEnabled()
        ? 'Buy & Hold and BRRRR figures are modeled from your assumptions. They are not a lender approval, a promise of profit, or a recommendation to purchase.'
        : 'DealSkout does not underwrite rental income.'}{' '}
      Automatic comps and AI Deal Review can be wrong or incomplete. Verify records, bids, and
      financing with your own sources.
    </Typography>
  </LegalLayout>
);

export const BillingPolicyPage: React.FC = () => (
  <LegalLayout title="Billing, cancellation, and refunds">
    <Typography paragraph>
      The Free plan does not require a card. DealSkout Pro, when you subscribe, is billed by Stripe
      for the period shown at checkout. Canceling at period end keeps paid access until that period
      ends. It does not erase deals you already saved.
    </Typography>
    <Typography paragraph>
      Request Credits are purchased separately and are not a change to the included AI or comp
      allowance. A refund of a credit purchase, when Stripe reports it, reduces the credit balance
      by the refunded amount. This draft does not promise refunds beyond what Stripe and the owner
      later approve.
    </Typography>
    <Typography paragraph>
      Promotion codes do not change plan allowances or your role. Unpaid or past-due subscriptions
      fall back to Free limits.
    </Typography>
  </LegalLayout>
);

export const ContactPage: React.FC = () => (
  <LegalLayout title="Contact">
    <Typography paragraph>
      {SUPPORT_EMAIL ? (
        <>
          Email{' '}
          <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link> for account help.
        </>
      ) : (
        'Support email is not configured yet. Set REACT_APP_SUPPORT_EMAIL before public launch.'
      )}
    </Typography>
  </LegalLayout>
);
